import {Injectable} from '@angular/core';
import {Claim, ClaimSelf, ClaimSelfAlt, ClaimSelfDetails, ClaimSelfForAdmin, ClaimSelfState, ClaimType, ClaimTypeClaimSelf, ClaimsSelf} from '../models/claim';
import {environment} from 'src/environments/environment';
import {IconProp} from '@satoris/ngx-satoris';
import {ApiService} from './api.service';
import {isDateExpired} from '../utils/date';

@Injectable({
  providedIn: 'root'
})
export class PersonnalDatasService {
  personnalDatas: any;
  dataToArray: any;

  countryClaims = Object.fromEntries(Object.entries(environment.selfClaims).filter(([_, value]) => value !== undefined));
  selfClaims = {} as ClaimsSelf[keyof ClaimsSelf];
  allCustomerSelfClaims = [] as ClaimSelfForAdmin[];
  scrollNumberOpenSelfClaims = 30;
  selfClaimsMerged: any = {} as ClaimsSelf[keyof ClaimsSelf];
  
  constructor(private api: ApiService) {}
  
  /**
   * Get a state and return the icon and the color of the state
   * @param state 
   * @returns 
  */
  stateVisual(claim: ClaimSelfDetails): { icon: IconProp, color: string } { 
    if(claim?.expiresAt) {
      if(isDateExpired(claim.expiresAt)) {
        return {
          icon: 'hourglass-end',
          color: 'var(--clr-btn-red)'
        };
      }
    }
    if(claim?.state?.value === ClaimSelfState.CHANGING) {
      return {
        icon: 'rotate',
        color: claim?.state?.changingRefusal ? 'var(--clr-btn-red)' :  'orange'
      };
    } else if(claim?.state?.value === ClaimSelfState.REJECTED) {
      return {
        icon: 'circle-xmark',
        color: 'var(--clr-btn-red)'
      };
    } else if(claim?.state?.value === ClaimSelfState.PENDING) {
      return {
        icon: 'clock',
        color: 'orange'
      };
    } else if(claim?.state?.value === ClaimSelfState.APPROVED) {
      return {
        icon: 'circle-check',
        color: 'green'
      };
    } else {
      return {
        icon: undefined,
        color: ''
      };
    }
  }

  /**
   * Get a claim and return the state of the claim
   * @param claim 
   * @returns 
   */
  checkValiditySelfClaims = (claim: ClaimSelf & Claim): ClaimSelfDetails['state'] => {
    const isSelfClaimsChanging = this.api.claims?.[claim.type] && !claim?.approved_by_user__id;
    const isExpired = claim?.expiresAt ? isDateExpired(claim.expiresAt) : false;
    if(isExpired) {
      return {
        value: ClaimSelfState.EXPIRED,
        user_id: null,
        rejectReason: null
      };
    }
    if(claim?.unique){
      return {
        value: ClaimSelfState.APPROVED,
        user_id: null,
        rejectReason: null
      };
    }
    if(isSelfClaimsChanging) {
      return {
        value: ClaimSelfState.CHANGING,
        changingRefusal: claim?.rejected_by_user__id ? true :  false,
        user_id: claim.rejected_by_user__id,
        rejectReason: claim?.rejectReason ? JSON.parse(claim?.rejectReason) : ''
      };
    } else if(claim.rejected_by_user__id) {
      return {
        value: ClaimSelfState.REJECTED,
        user_id: claim.rejected_by_user__id,
        rejectReason: claim?.rejectReason ? JSON.parse(claim?.rejectReason) : ''
      };
    } else if(claim.approved_by_user__id || claim?.signature && !isSelfClaimsChanging) {
      return {
        value: ClaimSelfState.APPROVED,
        user_id: claim.approved_by_user__id,
        rejectReason: null
      };
    } else {
      return {
        value: ClaimSelfState.PENDING,
        user_id: null,
        rejectReason: null
      };
    }
  };

  /**
   * Allow to transform a claim to a self claim
   * @param claims Claims to transform
   * @param needParent If we need to add the parent (ClaimAlt)
   * @param returnArray If we need to return an array
   * @param mergeArray If we need to merge the array
   * @param needIcon If we need to add the icon
   */
  transformClaimToSelfClaim(claims: Claim[] & ClaimSelf[], needParent = false, returnArray = false, mergeArray = false, needIcon = true, adminFormat = true) {
    let dataToReturn: any= returnArray ? [] : {};
    const isAdmin = this.api.userRole.isAdmin;
    claims.forEach((claim: Claim & ClaimSelf) => {
      const state = this.checkValiditySelfClaims(claim);
      claim.state = state;
      const deserializedData = claim.serialized ? JSON.parse(claim?.serialized) : '';
      let parentAlt: ClaimSelfAlt = null;
      let additionalDatas: ClaimSelfDetails = null;
      Object.keys(this.countryClaims).forEach(subType => {
        if(subType in ClaimSelfAlt) {
          if(claim.type in this.countryClaims[subType as ClaimSelfAlt]) {
            parentAlt = subType as ClaimSelfAlt;
            additionalDatas = (this.countryClaims as any)[subType][claim.type];
          } else {
            additionalDatas = (this.countryClaims as any)[claim.type];
          }
        }
      });
      const commonProperties: ClaimSelfDetails = {
        title: parentAlt ? this.countryClaims[parentAlt].label : this.countryClaims[+claim?.type]?.label,
        label: additionalDatas?.metadataValue[0].label,
        expiresAt: claim?.expiresAt,
        type: claim?.type,
        assignedAt: claim?.assignedAt,
        proofDocument: claim?.proof,
        icon: additionalDatas?.icon ? additionalDatas?.icon : additionalDatas.metadataValue[0].icon,
        stateIcon: needIcon && {
          icon: this.stateVisual(claim).icon,
          iconColor: this.stateVisual(claim).color
        },
        metadataValue: additionalDatas?.metadataValue,
        created_at: claim?.createdAt,
        value: deserializedData,
        arrayElement: [{
          subElements: additionalDatas?.metadataValue.map(metaData => ({
            title: 'form.DYN.' + metaData.label,
            value: claim?.serialized ? JSON.parse(claim.serialized)[metaData.label] : '',
            icon: metaData.icon,
            stateIcon: needIcon && parentAlt && mergeArray ? {
              icon: this.stateVisual(claim).icon,
              iconColor: this.stateVisual(claim).color
            } : undefined
          }))
        }],
        id: claim?.id,
        state
      };
      if(isAdmin && adminFormat) {
        if(needParent && parentAlt) {
          if(!dataToReturn[parentAlt]) {
            dataToReturn[parentAlt] = [];
          }
          dataToReturn[parentAlt].push(commonProperties);
        } else {
          if(!dataToReturn[claim.type]) {
            dataToReturn[claim.type] = [];
          }
          dataToReturn[claim.type].push(commonProperties);
        }
      } else {
        if(needParent && parentAlt) {
          dataToReturn[parentAlt] = {...dataToReturn[parentAlt], [claim.type]: commonProperties};
        } else {
          dataToReturn[claim.type] = commonProperties;
        }
      }
    });

    if(returnArray) {
      dataToReturn = Object.values(dataToReturn);
    }
    if(mergeArray) {
      const uniqueClaims: any = {};
      Object.values(dataToReturn).forEach((claim: ClaimSelfDetails) => {
        if(uniqueClaims[claim.title]) {
          uniqueClaims[claim.title].arrayElement[0].subElements.push(...claim.arrayElement[0].subElements);
        } else {
          uniqueClaims[claim.title] = claim;
        }
      });
      dataToReturn = Object.values(uniqueClaims).filter((claim: ClaimSelfDetails) => claim !== undefined && claim !== null);
    }
    return (isAdmin && adminFormat) ? dataToReturn.flat() : dataToReturn;
  }

  /**
   * Get the self claims of the customer and merge them with the self claims validated by the admin
   * @param selfClaims Claim self get from the server
   * @param claimSelfValidated Claim self validated by the admin
   * @returns 
   */
  selfClaimsCustomer(claimSelfValidated: ClaimSelf[]): Promise<boolean> {
    return this.api.mySelfClaims()
      .then((selfClaims: ClaimSelf[]) => {
        const seen = new Set();
        const filteredRes = selfClaims
          .sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime())
          .filter(claim => {
            const identifier = `${claim.user__id}-${claim.type}`;
            if(!seen.has(identifier)) {
              seen.add(identifier);
              return true;
            }
            return false;
          });
        const filteredResApproved = filteredRes.map(item => ({...item}));
        claimSelfValidated.forEach((claim: any) => {
          if(!filteredRes.find(c => c.type === claim.type)) {
            claim.unique = true;
            filteredRes.push(claim);
          }
        });
        for(let i = 0; i < filteredRes.length; i++) {
          const claim = filteredRes[i];
          const state = this.checkValiditySelfClaims(claim);
          claim.state = state;
          const foundClaim = claimSelfValidated.find((c: any) => c.type === claim.type);
          if(foundClaim) {
            filteredResApproved[i] = foundClaim;
            if(state.changingRefusal) {
              filteredResApproved[i].rejected_by_user__id = state.user_id;
            }
            if(state.value === ClaimSelfState.APPROVED) {
              filteredRes[i] = foundClaim;
              filteredRes[i].approved_by_user__id = state.user_id;
            }
          }
        }
        this.selfClaims = this.transformClaimToSelfClaim(filteredRes, true);
        this.selfClaimsMerged = this.transformClaimToSelfClaim(filteredResApproved, undefined, true, true);
        return true;
      });
  }

  /**
   * Get self claims for the admin
   * @param id optional id of the claim for fetch more details about it
   * @returns 
   */
  selfClaimsAdmin(id?: string): Promise<boolean> {
    return this.api.openSelfClaims(undefined, this.scrollNumberOpenSelfClaims - this.scrollNumberOpenSelfClaims, this.scrollNumberOpenSelfClaims).then(res => {
      if(res.result.length === 0) {
        this.allCustomerSelfClaims= [];
        return true;
      }
      if(!id) {
        this.allCustomerSelfClaims = this.transformClaimToSelfClaim(res.result, undefined, true);
      } else {
        return this.api.getSelfClaim(id).then(claim => {
          this.allCustomerSelfClaims = this.transformClaimToSelfClaim([claim], undefined, true);
          return true;
        });
      }
      return true;
    });
  }

  isClaimTypeClaimSelf(value: any): value is ClaimTypeClaimSelf {
    return value === ClaimType.ADDITIONAL_INFO_ADDRESS || 
           value === ClaimType.ADDITIONAL_INFO_CONTACT_EMAIL || 
           value === ClaimType.ADDITIONAL_INFO_CONTACT_PHONE_NR;
  }

  isHigherPriority(claim1: ClaimSelfDetails, claim2: ClaimSelfDetails): boolean {
    const statePriority = {
      [ClaimSelfState.EXPIRED]: 5,
      [ClaimSelfState.REJECTED]: 4,
      [ClaimSelfState.CHANGING]: 3,
      [ClaimSelfState.PENDING]: 2,
      [ClaimSelfState.APPROVED]: 1,
      [ClaimSelfState.NONE]: 0
    };
    const priority1 = statePriority[claim1.state?.value] || 0;
    const priority2 = statePriority[claim2.state?.value] || 0;
    return priority1 > priority2;
  }
}
